// POSES — resident positions as DATA, not animation files.
//
// Khari, 2026-08-04: "a table for all of these 'positions'. like 'barber' or
// 'sitting customer' or 'browsing customer'... These will be simple animations
// which are basically prone loops. If we could drive these programatically,
// that would be optimal so we don't have to store so many animations."
//
// THE FACT THAT MAKES IT WORK (verified across every *_rigged.glb — aigent79,
// akra, avva ×3, cosmina, devvin, junex ×2, kelsey, malaki, nyjah, rebel,
// samzie, sassy_la, spor1): all 16 rigged roster models share ONE identical
// 24-joint skeleton, plain names, no `mixamorig:` prefix (fal Meshy rig):
//
//   Hips · LeftUpLeg LeftLeg LeftFoot LeftToeBase · RightUpLeg RightLeg
//   RightFoot RightToeBase · Spine Spine01 Spine02 · LeftShoulder LeftArm
//   LeftForeArm LeftHand · RightShoulder RightArm RightForeArm RightHand ·
//   neck Head head_end headfront
//
// So one table drives every avatar, present and future. Adding a pose is a row
// here — never an export, never a download. Re-run the skeleton check if the
// rigging pipeline ever changes.
//
// ── Two things that stop a posed figure reading as a mannequin ──
// 1. LIFE IS PROCEDURAL TOO. `breath` drives a sine on the spine, `sway` a
//    slower one on the hips, plus a slow drift on the head. No baked loops.
// 2. IT IS DESYNCED PER BODY (phaseOffset) or a room pulses in unison, which
//    reads worse than stillness — the eye catches the rhythm immediately.
//
// ── Authoring notes ──
// DEGREES here (converted at apply time) because these get tuned by eye, and
// nobody eyeballs radians. Offsets are FROM THE BIND POSE, so an absent bone
// just means "leave it as rigged" — a pose only has to say what it changes.
//
// The numbers below are a STARTING POINT, deliberately. Poses are a LOOK; the
// plan has always been to build the engine and then tune against Khari's eye
// "until the barber looks like a barber". Every value is overridable live via
// URL param (see poseOverrides) so tuning never needs a rebuild.

// ⚠ THE BIND POSE IS A T-POSE. Arms start STRAIGHT OUT along ±X, not hanging.
// Every offset below is measured from that, which is why resting poses carry a
// ~70-76° Z rotation on the upper arms: without it a "sitting" body is just a
// T-pose that sat down (Khari's first salon screenshot, 2026-08-06 — everyone
// standing with their arms out, looking unposed while the engine was in fact
// working exactly as written).
//
// SIGN CONVENTION, still unverified against the rig: LeftArm +Z and RightArm −Z
// are assumed to swing the arms DOWN. If they go UP instead, negate the Z on
// both arms everywhere — one live check settles it for every pose at once:
//     ?pose.sitting.LeftArm=8,0,-74
//
// Fraction-of-body-height the whole figure drops. Sitting needs this: bending
// the legs does not lower the hips, so without it a seated body floats above
// the chair with its legs stuck out. Applied on top of AnimatedResident's
// existing `yOff` (which is what plants the feet on the floor in the first
// place), and scaled by height so it holds for a tall body and a short one.

export const POSES = {
  sitting: {
    label: 'Sitting',
    hint: 'A customer in the chair — waiting, being worked on, listening.',
    drop: 0.26,
    breath: 1, sway: 0.35,
    bones: {
      LeftUpLeg: [88, 0, 3], RightUpLeg: [88, 0, -3],   // thighs forward, horizontal
      LeftLeg: [-82, 0, 0], RightLeg: [-82, 0, 0],      // shins hang down from the knee
      LeftFoot: [-8, 0, 0], RightFoot: [-8, 0, 0],
      Spine: [-4, 0, 0],                                 // sits back a touch
      LeftArm: [66, 0, 0], RightArm: [66, 0, 0],         // down off the T, into the lap
      LeftForeArm: [0, 0, 22], RightForeArm: [0, 0, -22],
    },
  },

  reclined: {
    label: 'Reclined (wash chair)',
    hint: 'Tipped back at the basin — head back, legs out long.',
    drop: 0.3,
    breath: 1.2, sway: 0.15,
    bones: {
      // Spine/neck NEGATIVE X leans back — verified on screen 2026-08-06.
      Spine: [-20, 0, 0], Spine01: [-8, 0, 0],
      neck: [24, 0, 0], Head: [10, 0, 0],   // +X tips the chin UP (verified)
      LeftUpLeg: [52, 0, 3], RightUpLeg: [52, 0, -3],
      LeftLeg: [-28, 0, 0], RightLeg: [-28, 0, 0],
      LeftArm: [62, 0, 0], RightArm: [62, 0, 0],
      LeftForeArm: [0, 0, 14], RightForeArm: [0, 0, -14],
    },
  },

  barber: {
    label: 'Working (barber)',
    hint: 'Standing at the station, hands up and forward at head height.',
    drop: 0,
    breath: 1.1, sway: 0.5,
    bones: {
      // Down off the T, but only halfway — hands stay up at the customer's head.
      LeftArm: [-42, 0, 0], RightArm: [-42, 0, 0],
      LeftForeArm: [0, 0, -55], RightForeArm: [0, 0, 55],
      Spine: [5, 0, 0],          // leans INTO the chair (+X is forward)
      neck: [4, 0, 0],
    },
  },

  browsing: {
    label: 'Browsing',
    hint: 'Flipping a rack or a shelf — one hand up, head down and tilted.',
    drop: 0,
    breath: 0.9, sway: 0.7,
    bones: {
      LeftArm: [26, 0, 0], LeftForeArm: [0, 0, 40],   // reaching at the rack
      RightArm: [70, 0, 0],                            // the other hangs
      neck: [10, 0, -5], Head: [6, 0, 3],              // head down at what they hold
      Spine: [4, 0, 0],
    },
  },

  leaning: {
    label: 'Leaning',
    hint: 'Against a wall or counter — off duty, watching the room.',
    drop: 0.02,
    breath: 0.8, sway: 0.25,
    bones: {
      Spine: [-6, 0, 5], Spine01: [0, 0, 3],
      LeftUpLeg: [4, 0, 0], RightUpLeg: [-8, 0, 0], RightLeg: [-12, 0, 0],
      LeftArm: [70, 0, 0], RightArm: [64, 0, 0], RightForeArm: [0, 0, -26],
      neck: [-2, 6, 0],
    },
  },
};

export const POSE_KEYS = Object.keys(POSES);

const DEG = Math.PI / 180;

// Live tuning without a rebuild — the same convention the lamp rig and the
// bouncer offsets already use (?inkey, ?sguardx …). Two forms:
//   ?pose.barber.RightArm=-38,0,-26   one bone, degrees
//   ?pose.sitting.drop=0.3            the whole-body drop
// Read once at module load; a reload applies them.
function poseOverrides() {
  if (typeof window === 'undefined') return {};
  const out = {};
  const q = new URLSearchParams(window.location.search);
  for (const [k, v] of q.entries()) {
    if (!k.startsWith('pose.')) continue;
    const [, poseKey, field] = k.split('.');
    if (!poseKey || !field || !POSES[poseKey]) continue;
    const nums = v.split(',').map(Number);
    if (nums.some(Number.isNaN)) continue;
    out[poseKey] = out[poseKey] || {};
    if (field === 'drop' || field === 'breath' || field === 'sway') out[poseKey][field] = nums[0];
    else out[poseKey][field] = nums.slice(0, 3);
  }
  return out;
}
const OVERRIDES = poseOverrides();

// ?poses=0 — TURN THE WHOLE SYSTEM OFF. Poses are a look being tuned by eye,
// and a half-tuned look is worse than none: a badly-bent body reads as a broken
// game, not an unfinished one. This puts every resident back on its idle clip
// without touching a single saved placement, so the room is presentable while
// the numbers are still moving.
const POSES_OFF = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('poses') === '0';

/**
 * Resolve a pose key to the numbers the renderer needs, radians and all.
 * Returns null for an unknown or absent key — which is how "no pose, play the
 * idle clip as usual" is expressed. Never throw here: a typo in a placement's
 * `data.pose` must degrade to a normal standing NPC, not an empty room.
 */
export function resolvePose(key) {
  if (!key || POSES_OFF) return null;
  const base = POSES[key];
  if (!base) return null;
  const ov = OVERRIDES[key] || {};
  const bones = {};
  for (const [bone, deg] of Object.entries({ ...base.bones, ...ov })) {
    if (!Array.isArray(deg)) continue;               // drop/breath/sway land here too
    bones[bone] = [(deg[0] || 0) * DEG, (deg[1] || 0) * DEG, (deg[2] || 0) * DEG];
  }
  return {
    key,
    label: base.label,
    drop: ov.drop ?? base.drop ?? 0,
    breath: ov.breath ?? base.breath ?? 1,
    sway: ov.sway ?? base.sway ?? 0.5,
    bones,
  };
}

export default POSES;
